import { useEffect, useState } from 'react';

/**
 * Sub-routes shared by every screen that hosts the delivery flow under its
 * own base path (`/my-packs`, `/shipping`, …). Each maps to one screen:
 *
 *   <base>                       → { kind: 'index' }        — host page
 *   <base>/deliveries/new        → { kind: 'delivery-new' } — create form
 *   <base>/deliveries/:id        → { kind: 'delivery-show', id }
 *
 * The shape is identical across hosts — only the base path differs — so the
 * parser, the URL writer (with the design-constructor popstate mirror) and
 * the React hook are produced once by `createDeliveryRoute(basePath)`.
 */
export type DeliveryRoute =
  | { kind: 'index' }
  | { kind: 'delivery-new' }
  | { kind: 'delivery-show'; id: number };

export interface DeliveryRouter {
  parse: (pathname: string) => DeliveryRoute;
  toPath: (route: DeliveryRoute) => string;
  push: (route: DeliveryRoute) => void;
  replace: (route: DeliveryRoute) => void;
  /**
   * React hook mirroring the current route. Updates on browser back/forward
   * (popstate) and exposes navigation helpers that also write the URL.
   */
  useRoute: () => {
    route: DeliveryRoute;
    push: (route: DeliveryRoute) => void;
    replace: (route: DeliveryRoute) => void;
  };
}

export function createDeliveryRoute(basePath: string): DeliveryRouter {
  const INDEX_PATH = basePath;
  const DELIVERIES_NEW_PATH = `${basePath}/deliveries/new`;
  // Base paths are fixed literals (`/my-packs`, `/shipping`) with no regex
  // metacharacters, so interpolating directly is safe.
  const showPattern = new RegExp(`^${basePath}/deliveries/(\\d+)$`);

  function parse(pathname: string): DeliveryRoute {
    if (pathname === DELIVERIES_NEW_PATH) return { kind: 'delivery-new' };
    const showMatch = showPattern.exec(pathname);
    if (showMatch && showMatch[1]) {
      const id = Number(showMatch[1]);
      if (Number.isFinite(id) && id > 0) return { kind: 'delivery-show', id };
    }
    return { kind: 'index' };
  }

  function toPath(route: DeliveryRoute): string {
    switch (route.kind) {
      case 'delivery-new':
        return DELIVERIES_NEW_PATH;
      case 'delivery-show':
        return `${basePath}/deliveries/${route.id}`;
      case 'index':
      default:
        return INDEX_PATH;
    }
  }

  function writeHistory(route: DeliveryRoute, mode: 'push' | 'replace'): void {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const nextPath = toPath(route);
    const isNewPath = url.pathname !== nextPath;
    url.pathname = nextPath;
    // Strip the legacy `?view=…` query so URL state has a single source.
    url.searchParams.delete('view');
    if (mode === 'push') window.history.pushState(null, '', url);
    else window.history.replaceState(null, '', url);
    // SPA route swaps produce no document load, so the browser keeps the
    // previous window scroll — reset it like a real navigation would.
    if (isNewPath) window.scrollTo(0, 0);
    // Notify the design-constructor preview iframe — SPA navigation emits no
    // `load` event, so without this the parent's scene filter lags a step.
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: 'design-constructor-route', pathname: url.pathname },
        '*',
      );
    }
  }

  function push(route: DeliveryRoute): void {
    writeHistory(route, 'push');
  }

  function replace(route: DeliveryRoute): void {
    writeHistory(route, 'replace');
  }

  function useRoute() {
    const [route, setRoute] = useState<DeliveryRoute>(() =>
      typeof window === 'undefined'
        ? { kind: 'index' }
        : parse(window.location.pathname),
    );

    useEffect(() => {
      const handler = () => setRoute(parse(window.location.pathname));
      window.addEventListener('popstate', handler);
      return () => window.removeEventListener('popstate', handler);
    }, []);

    return {
      route,
      push: (next: DeliveryRoute) => {
        push(next);
        setRoute(next);
      },
      replace: (next: DeliveryRoute) => {
        replace(next);
        setRoute(next);
      },
    };
  }

  return { parse, toPath, push, replace, useRoute };
}
